import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Header from '../components/Header';
import Footer from '../components/Footer';

const LVRCalculator = () => {
  const [propertyValue, setPropertyValue] = useState(645000);
  const [loanAmount, setLoanAmount] = useState(480760);

  const lvr = propertyValue > 0 ? (loanAmount / propertyValue) * 100 : 0;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getLVRStatus = () => {
    if (lvr <= 80) {
      return {
        color: 'text-green-600',
        bg: 'bg-green-50',
        border: 'border-green-500',
        message: 'Excellent - No LMI required',
        description: 'Your LVR is 80% or below, which means you typically won\'t need to pay Lenders Mortgage Insurance.'
      };
    } else if (lvr <= 90) {
      return {
        color: 'text-yellow-600',
        bg: 'bg-yellow-50',
        border: 'border-yellow-500',
        message: 'Good - LMI may apply',
        description: 'Your LVR is between 80-90%. You may need to pay Lenders Mortgage Insurance, but most lenders will approve this loan.'
      };
    } else if (lvr <= 95) {
      return {
        color: 'text-orange-600',
        bg: 'bg-orange-50',
        border: 'border-orange-500',
        message: 'Caution - Higher LMI costs',
        description: 'Your LVR is between 90-95%. LMI will be required and will be more expensive. Fewer lenders may be available.'
      };
    } else {
      return {
        color: 'text-red-600',
        bg: 'bg-red-50',
        border: 'border-red-500',
        message: 'High Risk - Very limited options',
        description: 'Your LVR is above 95%. Very few lenders will approve this loan, and LMI costs will be very high.'
      };
    }
  };

  const status = getLVRStatus();

  return (
    <div>
      <Header />
      <div className="min-h-screen bg-page py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              LVR Calculator
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Calculate your Loan-to-Value Ratio to understand your borrowing position
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* Input Section */}
            <Card>
              <CardHeader>
                <CardTitle>Property & Loan Details</CardTitle>
                <CardDescription>Enter your property and loan information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label>Property Value ($)</Label>
                  <Input 
                    type="number"
                    value={propertyValue || ''}
                    onChange={(e) => setPropertyValue(parseFloat(e.target.value) || 0)}
                    className="text-lg"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    The current market value of your property
                  </p>
                </div>
                <div>
                  <Label>Total Loan Amount ($)</Label>
                  <Input 
                    type="number"
                    value={loanAmount || ''}
                    onChange={(e) => setLoanAmount(parseFloat(e.target.value) || 0)}
                    className="text-lg"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    The total amount you want to borrow (including LMI if applicable)
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Result Section */}
            <Card className={`border-4 ${status.border} ${status.bg}`}>
              <CardHeader>
                <CardTitle>Your LVR Result</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="text-center py-6">
                  <div className="text-6xl font-bold mb-2" style={{ color: status.color.replace('text-', '') }}>
                    {lvr.toFixed(2)}%
                  </div>
                  <div className={`text-xl font-semibold ${status.color}`}>
                    {status.message}
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-sm text-gray-700">
                    {status.description}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Information Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <Card>
              <CardHeader>
                <CardTitle>Calculation Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-700">Property Value:</span>
                  <span className="font-semibold">{formatCurrency(propertyValue)}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-700">Loan Amount:</span>
                  <span className="font-semibold">{formatCurrency(loanAmount)}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-700">Your Equity:</span>
                  <span className="font-semibold text-green-600">
                    {formatCurrency(propertyValue - loanAmount)}
                  </span>
                </div>
                <div className="flex justify-between py-3 bg-blue-50 px-3 rounded-lg">
                  <span className="font-bold text-gray-900">LVR:</span>
                  <span className="font-bold text-blue-600">{lvr.toFixed(2)}%</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>What is LVR?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-700 mb-3">
                  <strong>Loan-to-Value Ratio (LVR)</strong> is the percentage of the property's value that you're borrowing.
                </p>
                <p className="text-sm text-gray-700 mb-3">
                  <strong>Formula:</strong> LVR = (Loan Amount ÷ Property Value) × 100%
                </p>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-700">
                    <strong>Example:</strong> If you borrow $400,000 on a $500,000 property, your LVR is 80%.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* LVR Guide */}
          <Card>
            <CardHeader>
              <CardTitle>LVR Guidelines</CardTitle>
              <CardDescription>Understanding different LVR ranges</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border-l-4 border-green-500">
                  <div className="flex-1">
                    <div className="font-semibold text-green-900">Up to 80% LVR</div>
                    <p className="text-sm text-gray-700 mt-1">
                      • No Lenders Mortgage Insurance (LMI) required<br/>
                      • Best interest rates available<br/>
                      • Most lenders will approve
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
                  <div className="flex-1">
                    <div className="font-semibold text-yellow-900">80% - 90% LVR</div>
                    <p className="text-sm text-gray-700 mt-1">
                      • LMI usually required<br/>
                      • Competitive interest rates<br/>
                      • Most lenders still available
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg border-l-4 border-orange-500">
                  <div className="flex-1">
                    <div className="font-semibold text-orange-900">90% - 95% LVR</div>
                    <p className="text-sm text-gray-700 mt-1">
                      • Higher LMI costs<br/>
                      • Slightly higher interest rates<br/>
                      • Fewer lender options
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border-l-4 border-red-500">
                  <div className="flex-1">
                    <div className="font-semibold text-red-900">Above 95% LVR</div>
                    <p className="text-sm text-gray-700 mt-1">
                      • Very limited lender options<br/>
                      • Highest LMI costs<br/>
                      • May require guarantor or additional security
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default LVRCalculator;
